import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import MultiImageUpload from '../components/MultiImageUpload';
import { toast } from 'sonner';
import { ArrowLeft, Save, Images } from 'lucide-react';

export const EditListingPage = () => {
  const { id } = useParams();
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listing, setListing] = useState(null);
  const [formData, setFormData] = useState({
    player_name: '',
    sport: '',
    set_name: '',
    year: '',
    grade_company: '',
    grade_value: '',
    price: '',
    description: ''
  });
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchListing();
  }, [isAuthenticated, id]);

  const fetchListing = async () => {
    try {
      const response = await api().get(`/cards/${id}`);
      const data = response.data;
      
      // Check ownership
      if (data.seller_id !== user?.id) {
        toast.error('You can only edit your own listings');
        navigate('/dashboard');
        return;
      }
      
      // Check if sold
      if (data.status === 'sold') {
        toast.error('Cannot edit a sold listing');
        navigate('/dashboard');
        return;
      }
      
      setListing(data);
      setFormData({
        player_name: data.player_name || '',
        sport: data.sport || '',
        set_name: data.set_name || '',
        year: data.year?.toString() || '',
        grade_company: data.grade_company || '',
        grade_value: data.grade_value || '',
        price: data.price?.toString() || '',
        description: data.description || ''
      });
      setImages(data.images?.length > 0 ? data.images : (data.image_url ? [data.image_url] : []));
    } catch (error) {
      console.error('Failed to fetch listing:', error);
      toast.error('Failed to load listing');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.player_name || !formData.price) {
      toast.error('Player name and price are required');
      return;
    }
    
    setSaving(true);
    try {
      await api().put(`/cards/${id}`, {
        player_name: formData.player_name,
        sport: formData.sport,
        set_name: formData.set_name,
        year: parseInt(formData.year) || null,
        grade_company: formData.grade_company,
        grade_value: formData.grade_value,
        price: parseFloat(formData.price),
        description: formData.description,
        image_url: images[0] || null,
        images: images
      });
      
      toast.success('Listing updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;
  if (!listing) return null;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="stat-card p-8">
          <h1 className="font-heading font-bold text-3xl text-white mb-2">Edit Listing</h1>
          <p className="text-zinc-400 mb-8">Update your card listing details</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Images */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Card Photos</Label>
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <Images className="w-3 h-3" />
                    Up to 5 images
                  </span>
                </div>
                <MultiImageUpload
                  maxImages={5}
                  initialImages={images}
                  onImagesChange={handleImagesChange}
                />
              </div>
              
              {/* Right Column - Form Fields */}
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Player Name *</Label>
                  <Input
                    value={formData.player_name}
                    onChange={(e) => handleInputChange('player_name', e.target.value)}
                    placeholder="Michael Jordan"
                    className="bg-zinc-800 border-zinc-600"
                    required
                    data-testid="edit-player-name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Sport *</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) => handleInputChange('sport', value)}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="edit-sport">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Baseball">Baseball</SelectItem>
                        <SelectItem value="Basketball">Basketball</SelectItem>
                        <SelectItem value="Football">Football</SelectItem>
                        <SelectItem value="Hockey">Hockey</SelectItem>
                        <SelectItem value="Soccer">Soccer</SelectItem>
                        <SelectItem value="Pokemon">Pokemon</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Year</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', e.target.value)}
                      placeholder="2024"
                      className="bg-zinc-800 border-zinc-600"
                      data-testid="edit-year"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Set/Brand Name</Label>
                  <Input
                    value={formData.set_name}
                    onChange={(e) => handleInputChange('set_name', e.target.value)}
                    placeholder="Topps Chrome"
                    className="bg-zinc-800 border-zinc-600"
                    data-testid="edit-set-name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Grade Company</Label>
                    <Select
                      value={formData.grade_company}
                      onValueChange={(value) => handleInputChange('grade_company', value)}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="edit-grade-company">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PSA">PSA</SelectItem>
                        <SelectItem value="BGS">BGS</SelectItem>
                        <SelectItem value="SGC">SGC</SelectItem>
                        <SelectItem value="CGC">CGC</SelectItem>
                        <SelectItem value="Raw">Raw (Ungraded)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Grade Value</Label>
                    <Input
                      value={formData.grade_value}
                      onChange={(e) => handleInputChange('grade_value', e.target.value)}
                      placeholder="10, 9.5, etc."
                      className="bg-zinc-800 border-zinc-600"
                      data-testid="edit-grade-value"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Price (USD) *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      placeholder="99.99"
                      className="bg-zinc-800 border-zinc-600 pl-7"
                      required
                      data-testid="edit-price"
                    />
                  </div>
                  <p className="text-xs text-emerald-400 mt-1">You keep 100% - buyers pay the 3.5% fee</p>
                </div>
                
                <div>
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Add any additional details about the card..."
                    className="bg-zinc-800 border-zinc-600 h-24"
                    data-testid="edit-description"
                  />
                </div>
              </div>
            </div>
            
            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4 border-t border-zinc-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-primary"
                data-testid="save-listing-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditListingPage;
